import { Q } from '@fullstory/tomato';

export interface CaptureEnabled {
  onClickOff(): void; // Click outside of element
  contains(elem: HTMLElement): boolean;
  hideFromCapture(): boolean;
  // Called on scroll
  reposition(): void;
}

// To allow for Multiple FragilePopups/Panels to stack up without interfering with the ones below them.
// Handles outside clicks, as well as scrolling and escape keys.
export class Capture {
  static ignoreClicksOnThis: Q.OfOne;
  // for content that is rendered later in the DOM (eg. a popup), set an optional class to be searched for
  // and tested for containment
  static ignoreClicksOnClasses: { [className: string]: boolean } = {};

  private static captureStack = <CaptureEnabled[]>[];
  private static wired = false;

  static shouldIgnore(target: HTMLElement): boolean {
    if (Capture.ignoreClicksOnThis && Capture.ignoreClicksOnThis.contains(target)) {
      return true;
    }
    for (let className in Capture.ignoreClicksOnClasses) {
      if (Capture.ignoreClicksOnClasses[className] && target.classList.contains(className)) {
        return true;
      }
    }
    return false;
  }

  static start(panel: CaptureEnabled) {
    try {
      Capture._maybeSetup();
    } finally {
      this.captureStack.push(panel);
    }
  }

  static release(panel: CaptureEnabled) {
    try {
      Capture._maybeSetup();
    } finally {
      for (var i = 0; i < Capture.captureStack.length; i++) {
        if (Capture.captureStack[i] == panel) {
          Capture.captureStack.splice(i, 1);
          return;
        }
      }
    }
  }

  static _maybeHide(panel: CaptureEnabled) {
    if (panel.hideFromCapture()) {
      panel.onClickOff();
    }
  }

  static _maybeSetup() {
    if (Capture.wired) {
      return;
    }

    Capture.wired = true;
    window.addEventListener("mousedown", (e) => {
      if (Capture.captureStack.length == 0) { return; }

      var top = Capture.captureStack[Capture.captureStack.length - 1]!;
      var target = e.target;
      // TODO(jaime): This hack is to prevent a history navigation before registering a click on the admin panel.
      if (!top.contains(<HTMLElement>target) && !Capture.shouldIgnore(<HTMLElement>target)) {
        Capture._maybeHide(top);
      }
    }, true);

    window.addEventListener("scroll", (e) => {
      this.captureStack.forEach((p) => {
        p.reposition();
      });
    }, true);

    window.addEventListener("keydown", (e: KeyboardEvent) => {
      if (Capture.captureStack.length == 0) { return; }

      switch (e.keyCode) {
        case 27: // Escape key.
          var top = Capture.captureStack[Capture.captureStack.length - 1]!;
          Capture._maybeHide(top);
      }
    }, true);
  }
}

export interface PointerEvent {
  (x: number, y: number): void;
}

// Helper class that allows you to listen for mouse/touch down-up-move events, "capturing" out-of-bounds moves
// while the pointer's down, and releasing the capture as necessary.
export class DragCapture {
  private _down = false;
  private _captured = false;
  private _hovered = false;

  private _mousedown: (e: MouseEvent) => void;
  private _mouseup: (e: MouseEvent) => void;
  private _mousemove: (e: MouseEvent) => void;

  private _touchstart: (e: TouchEvent) => void;
  private _touchend: (e: TouchEvent) => void;
  private _touchmove: (e: TouchEvent) => void;

  constructor(private _elem: Q.OfOne, private _dragStart: PointerEvent, private _dragEnd: PointerEvent, private _drag: PointerEvent) {
    this._mousedown = (e: MouseEvent) => { this.pointerDown(e.clientX, e.clientY); e.preventDefault(); };
    this._mouseup = (e: MouseEvent) => { this.pointerUp(e.clientX, e.clientY); e.preventDefault(); };
    this._mousemove = (e: MouseEvent) => { this.pointerMove(e.clientX, e.clientY); e.preventDefault(); };

    this._touchstart = (e: TouchEvent) => { var t = e.changedTouches[0]!; this.pointerDown(t.clientX, t.clientY); e.preventDefault(); };
    this._touchend = (e: TouchEvent) => { var t = e.changedTouches[0]!; this.pointerUp(t.clientX, t.clientY); e.preventDefault(); };
    this._touchmove = (e: TouchEvent) => { var t = e.changedTouches[0]!; this.pointerMove(t.clientX, t.clientY); e.preventDefault(); };

    _elem.on("mousedown", this._mousedown);
    // TODO(stephanie or jaime): I think there's a bug here. If you're hovering the element that you're dragging when you release,
    // this callback is executed twice, once for the window and once for the window.
    _elem.on("mouseup", this._mouseup);
    _elem.on("mousemove", this._mousemove);

    _elem.on("touchstart", this._touchstart);
    _elem.on("touchend", this._touchend);
    _elem.on("touchmove", this._touchmove);

    _elem.on("mouseenter", () => {
      this._hovered = true;
      _elem.addClass("hover");
    });
    _elem.on("mouseleave", () => {
      this._hovered = false;
      if (!this._captured) {
        _elem.removeClass("hover");
      }
    });
  }

  private capture() {
    this._captured = true;
    this._elem.addClass("hover");
    window.addEventListener("mousedown", this._mousedown, true);
    window.addEventListener("mouseup", this._mouseup, true);
    window.addEventListener("mousemove", this._mousemove, true);
    window.addEventListener("touchstart", this._touchstart, true);
    window.addEventListener("touchend", this._touchend, true);
    window.addEventListener("touchmove", this._touchmove, true);
  }

  private release() {
    this._captured = false;
    if (!this._hovered) {
      this._elem.removeClass("hover");
    }
    window.removeEventListener("mousedown", this._mousedown, true);
    window.removeEventListener("mouseup", this._mouseup, true);
    window.removeEventListener("mousemove", this._mousemove, true);
    window.removeEventListener("touchstart", this._touchstart, true);
    window.removeEventListener("touchend", this._touchend, true);
    window.removeEventListener("touchmove", this._touchmove, true);
  }

  private pointerDown(x: number, y: number) {
    this._down = true;
    this.capture();
    this._dragStart(x, y);
  }

  private pointerUp(x: number, y: number) {
    this._down = false;
    this.release();
    this._dragEnd(x, y);
  }

  private pointerMove(x: number, y: number) {
    if (this._down) {
      this._drag(x, y);
    }
  }
}
